/**
 * Goal DEFINITION — the redesign's "make a goal mean something" layer, shared by
 * the goals ledger (list) and the goal detail page so the vocabulary matches.
 *
 * A goal carries an OUTCOME statement + a DEFINITION OF DONE in either (or both)
 * of two shapes — a target METRIC (baseline → now → target) and an acceptance
 * CHECKLIST. This module owns the parsing (DB columns → typed shapes) plus the
 * presentational readouts. Pure render + pure functions (no hooks, no server
 * imports) so it's safe in both RSC and client trees.
 */

import * as React from "react";
import { Progress } from "../../progress";
import { cn } from "../../utils";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  CircleSlash,
  ListChecks,
  Target,
} from "../../icons";

/** A single acceptance criterion. */
export interface GoalCriterion {
  label: string;
  done: boolean;
}

/** The raw definition columns as they come off `excellent_goals` (v83). */
export interface GoalDefinitionFields {
  outcome: string | null;
  why: string | null;
  metric_label: string | null;
  metric_baseline: string | null;
  metric_current: string | null;
  metric_target: string | null;
  metric_pct: number | null;
  criteria: string | null;
}

export interface GoalMetric {
  label: string;
  baseline: string | null;
  current: string;
  target: string;
  pct: number;
}

/** The compact, one-shape finish line used in a dense list row.
 *
 *  `criteria` is the generic "n of m things met" shape. It started as the
 *  acceptance checklist, and it is now also how a STRATEGIC goal's key results
 *  arrive (met = KRs at target) — same shape, same bar, so the two kinds of
 *  goal read identically. `noun` names the unit for the caption; it defaults to
 *  "criteria". */
export type GoalFinishLine =
  | ({ kind: "metric" } & GoalMetric)
  | { kind: "criteria"; met: number; total: number; noun?: string }
  | { kind: "none" };

/** Parse the `criteria` JSON column into a typed, defensive array. */
export function parseCriteria(json: string | null): GoalCriterion[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((x) => x && typeof x.label === "string")
      .map((x) => ({ label: String(x.label), done: !!x.done }));
  } catch {
    return [];
  }
}

/** A goal's metric, if it has one (needs at least a current + target). */
export function goalMetric(f: GoalDefinitionFields): GoalMetric | null {
  if (!f.metric_current || !f.metric_target) return null;
  return {
    label: f.metric_label ?? "",
    baseline: f.metric_baseline,
    current: f.metric_current,
    target: f.metric_target,
    pct: f.metric_pct ?? 0,
  };
}

/** The single finish line to show in a dense row: metric first, else checklist. */
export function buildFinishLine(f: GoalDefinitionFields): GoalFinishLine {
  const m = goalMetric(f);
  if (m) return { kind: "metric", ...m };
  const items = parseCriteria(f.criteria);
  if (items.length)
    return { kind: "criteria", met: items.filter((i) => i.done).length, total: items.length };
  return { kind: "none" };
}

// ── Compact (list-row) readout ───────────────────────────────────────────────

/** One-line finish-line readout for a dense queue row. */
export function FinishLineCompact({
  finishLine,
  onDefine,
}: {
  finishLine: GoalFinishLine;
  /** Href for the inline "Define" affordance on a thin goal. */
  onDefine?: string;
}) {
  if (finishLine.kind === "metric") {
    return (
      <div className="flex items-center gap-2">
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {finishLine.current}→{finishLine.target}
        </span>
        <Progress value={finishLine.pct} className="h-1 w-12" />
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {finishLine.pct}%
        </span>
      </div>
    );
  }
  if (finishLine.kind === "criteria") {
    const pct = Math.round((finishLine.met / finishLine.total) * 100);
    return (
      <div className="flex items-center gap-2">
        <span className="shrink-0 type-subtext tabular-nums">
          <span className="font-medium">
            {finishLine.met}/{finishLine.total}
          </span>{" "}
          <span className="text-muted-foreground">crit</span>
        </span>
        <Progress value={pct} className="h-1 w-12" />
      </div>
    );
  }
  // Thin — when the whole row is a link, render linkless text (no nested <a>);
  // when an explicit define href is given, render the inline action.
  if (onDefine) {
    return (
      <a
        href={onDefine}
        className="inline-flex items-center gap-1 type-subtext text-primary hover:underline"
      >
        <CircleSlash className="size-3" />
        Define
      </a>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 type-subtext text-muted-foreground">
      <CircleSlash className="size-3" />
      No finish line
    </span>
  );
}

// ── Full (detail-page) readout ───────────────────────────────────────────────

function MetricReadout({ metric }: { metric: GoalMetric }) {
  return (
    <div className="flex flex-col gap-3">
      {metric.label ? (
        <span className="type-body font-medium text-foreground">{metric.label}</span>
      ) : null}
      <div className="grid grid-cols-3 overflow-hidden rounded-md border border-border/50">
        {[
          { k: "Baseline", v: metric.baseline ?? "—", emphasize: false },
          { k: "Now", v: metric.current, emphasize: true },
          { k: "Target", v: metric.target, emphasize: false },
        ].map((cell, i) => (
          <div
            key={cell.k}
            className={cn(
              "flex flex-col gap-0.5 px-3 py-2.5",
              i < 2 && "border-r border-border/50",
              cell.emphasize && "bg-secondary/40",
            )}
          >
            <span className="type-subtext text-muted-foreground">{cell.k}</span>
            <span
              className={cn(
                "type-title font-medium tabular-nums",
                cell.emphasize ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {cell.v}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Progress value={metric.pct} className="h-1.5 flex-1" />
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {metric.pct}% to target
        </span>
      </div>
    </div>
  );
}

function CriteriaChecklist({ items }: { items: GoalCriterion[] }) {
  const met = items.filter((c) => c.done).length;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between type-subtext text-muted-foreground">
        <span>Acceptance criteria</span>
        <span className="tabular-nums">
          {met}/{items.length} met
        </span>
      </div>
      <ul className="flex flex-col">
        {items.map((c, i) => (
          <li
            key={i}
            className={cn(
              "flex items-start gap-2.5 py-2 type-body",
              i < items.length - 1 && "border-b border-border/40",
              i === 0 && "pt-0",
            )}
          >
            {c.done ? (
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
            ) : (
              <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground/40" />
            )}
            <span className={c.done ? "text-muted-foreground" : "text-foreground"}>
              {c.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The full Definition-of-done body for the goal detail page — a metric readout
 * and/or an acceptance checklist (both render when both are set), or a guiding
 * empty that prompts the user to add the finish line.
 */
export function DefinitionOfDone({
  fields,
  defineHref,
}: {
  fields: GoalDefinitionFields;
  defineHref?: string;
}) {
  const metric = goalMetric(fields);
  const items = parseCriteria(fields.criteria);
  const hasAny = metric || items.length > 0;

  if (!hasAny) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md border border-dashed border-border/70 px-3 py-3">
        <span className="type-body text-muted-foreground">
          No finish line yet — there&rsquo;s no way to tell when this goal is
          achieved. Add a target metric to move, or a checklist of acceptance
          criteria.
        </span>
        <a
          href={defineHref ?? "#"}
          className="inline-flex items-center gap-1.5 type-body text-primary hover:underline"
        >
          <Target className="size-3.5" />
          Add the finish line
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {metric ? <MetricReadout metric={metric} /> : null}
      {metric && items.length > 0 ? (
        <div className="border-t border-border/40" />
      ) : null}
      {items.length > 0 ? <CriteriaChecklist items={items} /> : null}
      <p className="flex items-start gap-2 border-t border-border/40 pt-3 type-subtext text-muted-foreground">
        <ListChecks className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Each linked issue passes ship/verify (a different agent signs off). The
          goal closes when every criterion is met — the reconciler does it
          automatically.
        </span>
      </p>
    </div>
  );
}

/** The outcome statement + why-it-matters block for the goal detail page. */
export function GoalOutcomeBlock({
  outcome,
  why,
  defineHref,
}: {
  outcome: string | null;
  why: string | null;
  defineHref?: string;
}) {
  if (!outcome) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md border border-dashed border-warning/50 bg-warning/[0.04] px-3 py-3">
        <span className="type-body font-medium text-warning-fg">
          This goal doesn&rsquo;t describe an outcome yet
        </span>
        <span className="type-body text-muted-foreground">
          It&rsquo;s a topic, not a goal. Say what will be true when it&rsquo;s
          done — one declarative sentence an agent could pursue and a verifier
          could check.
        </span>
        <a
          href={defineHref ?? "#"}
          className="inline-flex items-center gap-1.5 type-body text-primary hover:underline"
        >
          <ArrowRight className="size-3.5" />
          Write the outcome
        </a>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="max-w-3xl type-body leading-body text-foreground">{outcome}</p>
      {why ? (
        <p className="max-w-3xl border-l-2 border-border/60 pl-3 type-body text-muted-foreground">
          <span className="font-medium text-foreground/70">Why it matters — </span>
          {why}
        </p>
      ) : null}
    </div>
  );
}
